import { type Action, createGeneralActionsByTransfers } from '@0xtorch/core'
import type { Chain } from '../../chain'
import type { BlockEvent } from '../../types'
import { createPolkadotAddress, createPolkadotSource } from '../../utils'
import { convertBroadcastSwap } from './convertBroadcastSwap'
import { convertDappStakingRewards } from './convertDappStakingRewards'
import { convertXcmFeesPaid } from './convertXcmFeesPaid'
import { createGeneralTransfers } from './createGeneralTransfers'
import { handleEthereumExecuted } from './handleEthereumExecuted'
import { handleTransactionFee } from './handleTransactionFee'

type ParseExtrinsicToActionsParameters = {
  chain: Chain
  blockNumber: number
  timestamp: number
  extrinsicIndex: number | undefined
  events: readonly BlockEvent[]
  relatedAddresses: Set<string>
  debug?: (message: unknown) => void
}

export const parseExtrinsicToActions = ({
  chain,
  blockNumber,
  timestamp,
  extrinsicIndex,
  events,
  relatedAddresses,
  debug,
}: ParseExtrinsicToActionsParameters): readonly Action[] => {
  const source = createPolkadotSource({
    chainId: chain.id,
    blockNumber,
    extrinsicIndex,
  })

  // ethereum.Executed event がある場合は原則処理しない
  // ただし xTokens.TransferredAssets がある場合は assets.Burned event のみ処理
  if (
    events.some(
      (event) => event.section === 'ethereum' && event.method === 'Executed',
    )
  ) {
    const handleEthereumResult = handleEthereumExecuted({
      chain,
      timestamp,
      source,
      events,
      relatedAddresses,
      debug,
    })
    if (handleEthereumResult) {
      return resetAccountActionsOrder(handleEthereumResult.actions)
    }
    return []
  }

  const actions: Action[] = []
  let targetEvents = events

  // extrinsicIndex が定義されている場合に限り、最初の event が withdraw 系だった場合に該当 event を tx fee として処理・その後に渡す events から削除
  const handleTransactionFeeResult =
    extrinsicIndex === undefined
      ? undefined
      : handleTransactionFee({
          chain,
          timestamp,
          source,
          events: targetEvents,
          relatedAddresses,
          debug,
        })
  if (handleTransactionFeeResult) {
    actions.push(...handleTransactionFeeResult.actions)
    targetEvents = handleTransactionFeeResult.unhandledEvents
  }

  const eventConverters = [convertDappStakingRewards, convertXcmFeesPaid]
  for (const converter of eventConverters) {
    const result = converter({
      chain,
      timestamp,
      source,
      events: targetEvents,
      relatedAddresses,
      debug,
    })
    if (result !== undefined) {
      actions.push(...result.actions)
      targetEvents = result.unhandledEvents
    }
  }

  // converter で未処理となった event を transfer として生成
  const transfers = createGeneralTransfers({
    chain,
    events: targetEvents,
    relatedAddresses,
    debug,
  })
  const accountIds = new Set(
    [...relatedAddresses].map((address) =>
      createPolkadotAddress({
        chainId: chain.id,
        address,
      }),
    ),
  )

  const broadcastSwapActions = convertBroadcastSwap({
    timestamp,
    source,
    events,
    transfers,
    accountIds,
    hasTransactionFeeAction: actions.some(
      (action) => action.type === 'transaction-fee',
    ),
  })
  if (broadcastSwapActions !== undefined) {
    actions.push(...broadcastSwapActions)
    return resetAccountActionsOrder(actions)
  }

  // converter で処理できなかった場合に general actions として処理する
  const generalActions = createGeneralActionsByTransfers({
    transfers,
    accountIds,
    source,
    timestamp,
  })
  actions.push(...generalActions)

  // action order を設定し直して返す
  return resetAccountActionsOrder(actions)
}

const resetAccountActionsOrder = (
  actions: readonly Action[],
): readonly Action[] => {
  for (const [index, action] of actions.entries()) {
    action.order = index
  }
  return actions
}
